import { useCallback, useEffect, useRef } from 'react';

type UseIntervalCallback = () => void;

export default function useInterval(
  callback: UseIntervalCallback,
  interval = 0,
  // Stops scheduling frames rather than guarding inside the callback: a loop
  // that has nothing left to do should not keep waking the browser up.
  isEnabled = true,
) {
  const callbackRef = useRef<UseIntervalCallback>(callback);
  const intervalRef = useRef<number>(interval);

  const previousRef = useRef<number | undefined>(undefined);
  const requestRef = useRef<number | undefined>(undefined);

  const tick = useCallback((now: number) => {
    if (!previousRef.current) {
      previousRef.current = now;
    }
    if (previousRef.current && (now - previousRef.current) >= intervalRef.current) {
      callbackRef.current();
      previousRef.current = now;
    }
    requestRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    intervalRef.current = interval;
  }, [interval]);

  useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }
    // Dropped so a loop that resumes does not immediately fire for the time it
    // spent stopped.
    previousRef.current = undefined;
    requestRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(requestRef.current!);
    };
  }, [isEnabled, tick]);
}
